'use client';

/**
 * @fileoverview Chat session list — renders as a dropdown from the
 * panel header. Each row supports switching to the session, renaming
 * (inline), or deleting (with a confirm step).
 *
 * Kept intentionally compact: this isn't the full conversation-history
 * explorer from a dedicated page — it's the "which chat am I in?"
 * control for the side panel.
 */

import { useState } from 'react';
import { Check, MoreHorizontal, Pencil, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import type { ChatSessionSummary } from '@/domains/chat/services';
import { cn } from '@/lib/utils';

interface SessionPickerProps {
  sessions: ChatSessionSummary[];
  activeSessionId: string | null;
  onSelect: (sessionId: string) => void;
  onNewChat: () => void;
  onRename: (sessionId: string, title: string) => void;
  onDelete: (sessionId: string) => void;
  /** The current session's display title, shown in the trigger. */
  activeTitle: string;
}

export function SessionPicker({
  sessions,
  activeSessionId,
  onSelect,
  onNewChat,
  onRename,
  onDelete,
  activeTitle,
}: SessionPickerProps) {
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');

  const startRename = (session: ChatSessionSummary) => {
    setRenamingId(session.id);
    setRenameValue(session.title);
  };

  const commitRename = () => {
    if (renamingId && renameValue.trim()) {
      onRename(renamingId, renameValue.trim());
    }
    setRenamingId(null);
    setRenameValue('');
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="max-w-[70%] justify-start gap-1">
          <span className="truncate font-medium">{activeTitle}</span>
          <MoreHorizontal className="h-3.5 w-3.5 flex-shrink-0 opacity-60" />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="start" className="w-72">
        <DropdownMenuItem onSelect={onNewChat} className="gap-2">
          <Plus className="h-4 w-4" />
          New chat
        </DropdownMenuItem>

        {sessions.length > 0 && <DropdownMenuSeparator />}

        <div className="max-h-[320px] overflow-y-auto">
          {sessions.map((session) => {
            const isActive = session.id === activeSessionId;
            const isRenaming = session.id === renamingId;

            if (isRenaming) {
              return (
                <div key={session.id} className="flex items-center gap-1 px-2 py-1">
                  <Input
                    autoFocus
                    value={renameValue}
                    onChange={(e) => setRenameValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        commitRename();
                      } else if (e.key === 'Escape') {
                        setRenamingId(null);
                      }
                    }}
                    onBlur={commitRename}
                    className="h-7 text-sm"
                  />
                </div>
              );
            }

            return (
              <div
                key={session.id}
                className={cn(
                  'group flex items-center gap-1 rounded-sm px-2 py-1 text-sm',
                  isActive && 'bg-accent',
                )}
              >
                <button
                  type="button"
                  onClick={() => onSelect(session.id)}
                  className="flex-1 min-w-0 text-left"
                >
                  <div className="flex items-center gap-2">
                    {isActive && <Check className="h-3.5 w-3.5 flex-shrink-0" />}
                    <span className="truncate">{session.title}</span>
                  </div>
                  <div className="text-xs text-muted-foreground pl-5">
                    {session.messageCount === 0
                      ? 'Empty'
                      : `${session.messageCount} message${session.messageCount === 1 ? '' : 's'}`}
                  </div>
                </button>

                <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={(e) => {
                      e.stopPropagation();
                      startRename(session);
                    }}
                    aria-label="Rename chat"
                  >
                    <Pencil className="h-3 w-3" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 text-destructive"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(session.id);
                    }}
                    aria-label="Delete chat"
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
